import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import {
  fetchProjectsByResource,
  removeResourceFromProject,
} from '../../store/actions/Projects/ProjectActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import {
  PiPlusLight,
  PiProjectorScreenChartLight,
  PiXLight,
  PiLinkBreakLight,
} from 'react-icons/pi';

// Format a backend resource type token for display.
//   "review_session" → "Review"
//   "research_chat"  → "Research Chat"
//   "data_source"    → "Data Source"
const formatResourceType = (raw: string): string => {
  if (!raw) return '';
  const lower = String(raw).trim().toLowerCase();
  if (lower === 'review_session') return 'Review';
  return lower
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

interface ProjectBadgesProps {
  resourceType: string;
  resourceUuid: string;
  /**
   * The resource has a link of ANOTHER kind (a task on a matter).
   *
   * Only affects the empty state: "Not linked" is a statement about the
   * record as a whole, so a task that is on a matter but no project is not
   * "not linked" - it just has no project, and keeps the quiet "Add to
   * project" affordance.
   */
  hasOtherLink?: boolean;
  /** How many projects this resource is on, whenever that changes. */
  onProjectCountChange?: (count: number) => void;
}

const ProjectBadges: React.FC<ProjectBadgesProps> = ({
  resourceType,
  resourceUuid,
  hasOtherLink = false,
  onProjectCountChange,
}) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!resourceType || !resourceUuid) return;
    const result = await dispatch(fetchProjectsByResource(resourceType, resourceUuid));
    if (result && Array.isArray(result)) {
      setProjects(result);
      onProjectCountChange?.(result.length);
    }
    setIsLoaded(true);
  }, [dispatch, resourceType, resourceUuid, onProjectCountChange]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  /**
   * Opens the shared side panel instead of the hover dropdown this used to
   * render. The badges stay the READ view; every write (link, unlink,
   * search across projects) happens in the panel, where there is room for it.
   */
  const handleOpenPanel = () => {
    dispatch(
      showSidePanel({
        type: 'connectToProject',
        showSidePanel: true,
        size: 'sm',
        resourceType,
        resourceUuid,
        message: t('Add to project'),
        subMsg: t('Link this {{type}} to one or more projects.', {
          type: formatResourceType(resourceType).toLowerCase(),
        }),
        icon: <PiProjectorScreenChartLight className="w-6 h-6 text-fg-muted" />,
        onChanged: () => { loadProjects(); },
        onCancel: () => dispatch(hideSidePanel()),
      })
    );
  };

  const handleUnlink = (projectUuid: string, projectTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const typeLabel = formatResourceType(resourceType);
    dispatch(
      showModal({
        showModal: true,
        type: 'confirmation',
        message: t('Unlink {{type}}', { type: typeLabel }),
        subMsg: t(
          'Are you sure you want to unlink this {{type}} from "{{project}}"? Collaborators may lose any access granted through this project.',
          { type: typeLabel.toLowerCase(), project: projectTitle }
        ),
        icon: <PiLinkBreakLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          try {
            await dispatch(removeResourceFromProject(projectUuid, resourceType, resourceUuid));
            await loadProjects();
          } finally {
            dispatch(hideModal());
          }
        },
        onCancel: () => dispatch(hideModal()),
      })
    );
  };

  // Show a small skeleton placeholder while loading
  if (!isLoaded) {
    return (
      <div className="flex items-center gap-1.5">
        <PiProjectorScreenChartLight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
        <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-md animate-fadePulse" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap relative">
      <PiProjectorScreenChartLight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
      {(projects.length < 3 ? projects : projects.slice(0, 2)).map((project) => (
        <button
          key={project.uuid}
          onClick={() => history.push(`/projects/${project.uuid}`)}
          className="group inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors cursor-pointer"
        >
          {project.title}
          <PiXLight
            className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:!opacity-100 transition-opacity"
            onClick={(e) => handleUnlink(project.uuid, project.title, e)}
          />
        </button>
      ))}
      {projects.length >= 3 && (
        <div className="relative group/overflow">
          <button className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-surface-sunken/70 text-gray-600 dark:text-gray-300 hover:bg-surface-soft transition-colors cursor-pointer">
            +{projects.length - 2} {t('more')}
          </button>
          <div className="glass hidden group-hover/overflow:block absolute top-full start-0 mt-1 z-50 w-56 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
            {projects.slice(2).map((project) => (
              <div key={project.uuid} className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-sunken/70 group/item">
                <button
                  onClick={() => history.push(`/projects/${project.uuid}`)}
                  className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1 text-start"
                >
                  {project.title}
                </button>
                <PiXLight
                  className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover/item:opacity-60 hover:!opacity-100 cursor-pointer flex-shrink-0 ms-2 transition-opacity"
                  onClick={(e) => handleUnlink(project.uuid, project.title, e)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/*
        NOT LINKED IS A STATE, NOT AN INVITATION.

        With no project AND no link of any other kind, the slot said "+ Add to
        project" - an action label standing in for a fact the record was never
        told. It reads as a badge stating what is true, and pressing it opens
        the same picker the plus does.
      */}
      {projects.length === 0 && !hasOtherLink ? (
        <button
          onClick={handleOpenPanel}
          title={t('Link this to a project')}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md border border-hairline bg-surface-sunken/70 text-gray-500 dark:text-gray-400 hover:border-champagne/50 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          {t('Not linked')}
        </button>
      ) : (
        <button
          onClick={handleOpenPanel}
          title={t('Add to project')}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] rounded-md text-gray-400 dark:text-gray-500 hover:bg-surface-sunken/70 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <PiPlusLight className="w-3 h-3" />
          {projects.length === 0 && t('Add to project')}
        </button>
      )}
    </div>
  );
};

export default ProjectBadges;
